import { useFormContext } from 'react-hook-form';
import { P, Section, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CountryListSelectInputOptions } from '../../../../Lib/Constants/countries';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { ItemDetailsFormData } from './types';
import { useGetMetaDatas } from '../../../../Lib/Hooks/MetadataService';
import { useGetVendorItem } from '../../../../Lib/Hooks/ItemService';

export function LogisticsSection() {
  const { control } = useFormContext<ItemDetailsFormData>();
  const { data: metadatas } = useGetMetaDatas(['ns_master_vendorsMerchType']);
  const { id: currentUserId, itemId: currentItemId } = useParams<{
    id: string;
    itemId: string;
  }>();

  const { data: targetItem } = useGetVendorItem(currentUserId, currentItemId);

  const vendor = useMemo(() => {
    if (metadatas?.ns_master_vendorsMerchType && targetItem) {
      const vendorFound = metadatas?.ns_master_vendorsMerchType.find(
        (vendorMerchType) =>
          vendorMerchType.vendorId === targetItem?.vendor_internal_id,
      );
      return vendorFound ?? null;
    }
    return null;
  }, [metadatas?.ns_master_vendorsMerchType, targetItem]);

  const countryOfOriginOptions = useMemo(() => {
    if (!vendor?.domesticMerchPoEligible) {
      return CountryListSelectInputOptions.filter(
        (country) => country.value !== 'US',
      );
    }
    return CountryListSelectInputOptions;
  }, [vendor]);

  return (
    <Section
      icon="truck/truck-default"
      title="Logistics"
      color="neutral"
      className="w-full flex flex-col"
    >
      <Stack direction="col" gap="md">
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="incoterm"
            label="Incoterm"
            control={control}
            inputType="multiselect"
            required
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: [
                { label: 'DDP', value: 'DDP' },
                { label: 'FCA', value: 'FCA' },
                { label: 'FOB', value: 'FOB' },
              ],
            }}
          />
          <ControlledFormInput
            name="countryOfOrigin"
            label="Country of Origin"
            control={control}
            inputType="multiselect"
            required
            disabled={countryOfOriginOptions.length <= 0}
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: countryOfOriginOptions,
            }}
          />
          <ControlledFormInput
            name="shipmentMode"
            label="Shipment Mode"
            control={control}
            inputType="multiselect"
            required
            inputProps={{
              searchable: true,
              singleSelectWhenSearchable: true,
              className: 'w-full',
              placeholder: 'Select..',
              options: [
                { label: 'Sea', value: 'Sea' },
                { label: 'Air', value: 'Air' },
                { label: 'Land', value: 'Land' },
              ],
            }}
          />
        </Stack>
        <Stack direction="row" gap="md">
          <ControlledFormInput
            name="leadTime"
            label="Lead Time"
            control={control}
            inputType="number"
            required
            inputProps={{
              className: 'w-full',
              min: '0',
              endAdornment: (
                <P size="xs" color="disabled">
                  weeks
                </P>
              ),
            }}
          />
          <ControlledFormInput
            name="reorderLeadTime"
            label="Reorder Lead Time"
            control={control}
            inputType="number"
            required
            inputProps={{
              placeholder: '0',
              className: 'w-full',
              min: '0',
              endAdornment: (
                <P size="xs" color="disabled">
                  weeks
                </P>
              ),
            }}
          />
          <ControlledFormInput
            name="cancelDate"
            label="Cancel Date"
            control={control}
            inputType="date"
            required
            inputProps={{
              placeholder: 'Add',
              containerClassName: 'w-full',
              className: 'w-full',
            }}
          />
        </Stack>
      </Stack>
    </Section>
  );
}
